import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../../components/ui/PageLayout';
import MetricsOverview from './components/MetricsOverview';
import ConversionFunnel from './components/ConversionFunnel';
import LeadSourceAttribution from './components/LeadSourceAttribution';
import CommunicationAnalytics from './components/CommunicationAnalytics';

import RealtimeUpdates from './components/RealtimeUpdates';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'funnel', label: 'Conversion Funnel', icon: 'GitBranch' },
    { id: 'sources', label: 'Lead Sources', icon: 'PieChart' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' },
    { id: 'realtime', label: 'Real-time', icon: 'Activity' }
  ];

  const loadAnalyticsData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString();

      const [leadsResult, campaignsResult, logsResult] = await Promise.allSettled([
        supabase?.from('funnel_leads')?.select('id, lead_score, is_converted, source, created_at')?.eq('user_id', user?.id)?.gte('created_at', since),
        supabase?.from('communication_campaigns')?.select('id, name, type, status, recipient_count, created_at')?.eq('user_id', user?.id)?.gte('created_at', since),
        supabase?.from('communication_logs')?.select('id, communication_type, status, created_at')?.eq('user_id', user?.id)?.gte('created_at', since)
      ]);

      const leads = leadsResult?.status === 'fulfilled' ? (leadsResult?.value?.data || []) : [];
      const campaigns = campaignsResult?.status === 'fulfilled' ? (campaignsResult?.value?.data || []) : [];
      const logs = logsResult?.status === 'fulfilled' ? (logsResult?.value?.data || []) : [];

      const totalLeads = leads?.length;
      const convertedLeads = leads?.filter(l => l?.is_converted)?.length;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100)?.toFixed(1) : 0;

      // Source breakdown
      const sourceMap = {};
      leads?.forEach(lead => {
        const src = lead?.source || 'direct';
        sourceMap[src] = (sourceMap?.[src] || 0) + 1;
      });

      setAnalyticsData({
        totalLeads,
        convertedLeads,
        conversionRate: parseFloat(conversionRate),
        activeCampaigns: campaigns?.filter(c => c?.status === 'active')?.length,
        totalCampaigns: campaigns?.length,
        totalCommunications: logs?.length,
        sourceBreakdown: Object.entries(sourceMap)?.map(([source, count]) => ({ source, count })),
        leads,
        campaigns,
        logs
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [user?.id, dateRange]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  return (
    <PageLayout>
      <Helmet>
        <title>Analytics Dashboard - LeadGen Pro</title>
        <meta name="description" content="Comprehensive analytics and performance insights for your lead generation campaigns" />
      </Helmet>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Comprehensive performance insights for your lead generation</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {['7d', '30d', '90d']?.map(range => (
            <Button
              key={range}
              variant={dateRange === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(range)}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </Button>
          ))}
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadAnalyticsData}>Refresh</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="AlertCircle" size={18} className="text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={loadAnalyticsData}>Retry</Button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {activeTab === 'overview' && <MetricsOverview data={analyticsData} />}
          {activeTab === 'funnel' && <ConversionFunnel data={analyticsData} />}
          {activeTab === 'sources' && <LeadSourceAttribution data={analyticsData} />}
          {activeTab === 'communications' && <CommunicationAnalytics data={analyticsData} />}
          {activeTab === 'realtime' && <RealtimeUpdates userId={user?.id} />}
        </div>
      )}
    </PageLayout>
  );
};

export default AnalyticsDashboard;